import {
  VersionRowBase,
  VersionStatus,
  VersioningBase
} from '../shared/versioning/types'
import { FILL_LEVELS, FillLevel } from '../shared/dosing_modes/fillLevels'

export const EMERGENCY_STRATEGIES = [
  'temporary_boost',
  'hold_until_slow_mode',
  'hold_until_stop_advance'
] as const
export type EmergencyStrategy = (typeof EMERGENCY_STRATEGIES)[number]

const LEVEL_NUMERIC_KEYS = [
  'fast_pressure',
  'slow_pressure',
  'stop_advance_grams',
  'max_dosing_duration_ms',
  'no_change_max_time_ms',
  'no_change_amount',
  'emergency_pressure',
  'max_emergency_duration_ms',
  'emergency_residual_delivery_amount'
] as const
export type LevelNumericKey = (typeof LEVEL_NUMERIC_KEYS)[number]

type NumericInput = number | string

export type LevelValues = Record<LevelNumericKey, NumericInput> & {
  id: number | null
  fill_level: FillLevel
  emergency_enabled: boolean
  emergency_strategy: EmergencyStrategy | ''
}

export interface SauceDosingModeValues {
  name: string
  description: string
  high_fill_min_percent: NumericInput
  medium_fill_min_percent: NumericInput
  unknown_fill_fallback: FillLevel | ''
  max_acceptable_shortfall_percent: NumericInput
  levels: LevelValues[]
}

type LevelResponse = Record<LevelNumericKey, number | string | null> & {
  id: number
  fill_level: FillLevel
  emergency_enabled: boolean
  emergency_strategy: EmergencyStrategy | null
}

export interface SauceDosingModeResponse {
  id: number
  name: string
  description: string | null
  status: VersionStatus
  version_number: number
  high_fill_min_percent: number | null
  medium_fill_min_percent: number | null
  unknown_fill_fallback: FillLevel | null
  max_acceptable_shortfall_percent: number
  levels: LevelResponse[]
}

export interface CopySourceSauceDosingMode extends SauceDosingModeResponse {
  average_rating: number | null
  comments_count: number
}

interface VersionRow extends VersionRowBase {
  averageRating: number | null
  commentsCount: number
}

export interface VersioningProps extends VersioningBase<VersionRow> {
  usedByProducts: string[]
  affectedLocations: string[]
}

export const isNumber = (value: unknown): value is number =>
  typeof value === 'number' && !Number.isNaN(value)

const blankIfMissing = (
  value: number | string | null | undefined
): NumericInput => (value === null || value === undefined ? '' : value)

const numericLevelFields = (level?: LevelResponse) =>
  Object.fromEntries(
    LEVEL_NUMERIC_KEYS.map((key) => [key, blankIfMissing(level?.[key])])
  ) as Record<LevelNumericKey, NumericInput>

const toLevelValues = (
  fillLevel: FillLevel,
  level?: LevelResponse
): LevelValues => ({
  ...numericLevelFields(level),
  id: level?.id ?? null,
  fill_level: fillLevel,
  emergency_enabled: level?.emergency_enabled ?? false,
  emergency_strategy: level?.emergency_strategy ?? ''
})

export const emptyMode = (): SauceDosingModeValues => ({
  name: '',
  description: '',
  high_fill_min_percent: '',
  medium_fill_min_percent: '',
  unknown_fill_fallback: '',
  max_acceptable_shortfall_percent: '',
  levels: FILL_LEVELS.map((fillLevel) => toLevelValues(fillLevel))
})

export const toFormValues = (
  mode: SauceDosingModeResponse
): SauceDosingModeValues => ({
  name: mode.name,
  description: mode.description ?? '',
  high_fill_min_percent: blankIfMissing(mode.high_fill_min_percent),
  medium_fill_min_percent: blankIfMissing(mode.medium_fill_min_percent),
  unknown_fill_fallback: mode.unknown_fill_fallback ?? '',
  max_acceptable_shortfall_percent: mode.max_acceptable_shortfall_percent,
  levels: FILL_LEVELS.map((fillLevel) =>
    toLevelValues(
      fillLevel,
      mode.levels.find((level) => level.fill_level === fillLevel)
    )
  )
})

export const toPayload = ({ levels, ...mode }: SauceDosingModeValues) => ({
  sauce_dosing_mode: { ...mode, levels_attributes: levels }
})

// Every parameter of the source replaces the form's; name, description and the current
// level ids stay so the update keeps editing the same mode and its existing levels.
export const copyParameters = (
  current: SauceDosingModeValues,
  source: SauceDosingModeResponse
): SauceDosingModeValues => {
  const copied = toFormValues(source)

  return {
    ...copied,
    name: current.name,
    description: current.description,
    levels: copied.levels.map((level) => ({
      ...level,
      id:
        current.levels.find(
          (currentLevel) => currentLevel.fill_level === level.fill_level
        )?.id ?? null
    }))
  }
}
